import React, { useMemo } from 'react';
import ITagGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDetail';
import ITagDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagDetail';
import { Guid } from '@Shared.Angular/@types/guid';
import Panel from './Panel/Panel';

interface Props {
  allTagGroups: ITagGroupDetail[];
  selectedTags: Guid[];
  onChange: (tagGuids: Guid[]) => void;
}

const TagSelect = ({ allTagGroups, selectedTags, onChange }: Props) => {
  const allTagsMap = useMemo(() => {
    const map = new Map<string, ITagDetail>();
    allTagGroups.forEach((group) =>
      group.tags.forEach((tag) => {
        map.set(tag.id, tag);
      })
    );
    return map;
  }, [allTagGroups]);

  const unselectedTags = useMemo(() => {
    return allTagGroups.map((group) => ({
      id: group.id,
      name: group.name,
      color: group.color,
      tags: group.tags.filter((tag) => !selectedTags.includes(tag.id))
    }));
  }, [allTagGroups, selectedTags]);

  const selectedTagObjects = useMemo(() => {
    return selectedTags
      .map((id) => allTagsMap.get(id))
      .filter((t): t is ITagDetail => !!t);
  }, [selectedTags, allTagsMap]);

  const selectedGrouped = useMemo(() => {
    const grouped: Record<
      string,
      { tags: ITagDetail[]; color: string; id: Guid }
    > = {};
    selectedTagObjects.forEach((tag) => {
      const group = allTagGroups.find((g) =>
        g.tags.some((t) => t.id === tag.id)
      );
      if (group) {
        if (!grouped[group.name]) {
          grouped[group.name] = {
            tags: [],
            id: group.id,
            color: group.color
          };
        }
        grouped[group.name].tags.push(tag);
      }
    });
    return grouped;
  }, [selectedTagObjects, allTagGroups]);

  const handleAdd = (tag: ITagDetail) => {
    if (!selectedTags.includes(tag.id)) {
      onChange([...selectedTags, tag.id]);
    }
  };

  const handleRemove = (tag: ITagDetail) => {
    onChange(selectedTags.filter((id) => id !== tag.id));
  };

  return (
    <div className="tag-select-container">
      <div className="available-tags-container">
        <Panel
          title="Find Tags"
          groups={unselectedTags}
          onTagClick={handleAdd}
          iconClass="fa-light fa-circle-plus fa-lg"
          backgroundColor="#f3f3f3"
        />
      </div>
      <div className="selected-tags-container">
        <Panel
          title="Selected Tags"
          groups={Object.entries(selectedGrouped).map(([name, info]) => ({
            id: info.id,
            name,
            color: info.color,
            tags: info.tags
          }))}
          onTagClick={handleRemove}
          iconClass="fa-light fa-circle-minus fa-lg"
          backgroundColor="#ddf4de"
        />
        <div className="remove-all-container">
          <button className="remove-all-button" onClick={() => onChange([])}>
            Remove All
          </button>
        </div>
      </div>
    </div>
  );
};

export default TagSelect;
